import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { PhotoCamera, Person } from '@mui/icons-material';
import { UserPublicProfile } from '../services/buddyService';
import { userProfileService, profilePhotoUrl } from '../services/userProfileService';
import { useDiveSiteCatalog } from '../hooks/useDiveSiteCatalog';
import { useNotification } from './NotificationContext';
import { resolveHomeSiteId } from '../utils/regions';
import { reportHomeRegion } from '../utils/regionSync';

interface ProfileEditTabProps {
  profile: UserPublicProfile;
  onUpdated: () => void;
}

const SPECIALTY_SUGGESTIONS = [
  'Wreck Diving', 'Underwater Photography', 'Night Diving', 'Freediving',
  'Cave Diving', 'Drift Diving', 'Ice Diving', 'Technical Diving',
  'Marine Biology', 'Spearfishing', 'Deep Diving', 'Search & Recovery',
];

// The Profile tab on the profile page — this is the former EditProfileDialog's
// content, relocated from a modal to an always-visible tab body. Fields here are
// still edited and explicitly saved via the button at the bottom, not auto-saved
// per-keystroke.
const ProfileEditTab: React.FC<ProfileEditTabProps> = ({ profile, onUpdated }) => {
  const { allSites } = useDiveSiteCatalog();
  const { showSuccess, showError } = useNotification();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [nickname, setNickname] = useState('');
  const [bio, setBio] = useState('');
  const [specialtyTags, setSpecialtyTags] = useState<string[]>([]);
  const [homeSiteName, setHomeSiteName] = useState('');
  const [divingSinceYear, setDivingSinceYear] = useState('');
  const [staticApnea, setStaticApnea] = useState('');
  const [dynamicApnea, setDynamicApnea] = useState('');
  const [freedivingDepth, setFreedivingDepth] = useState('');

  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setNickname(profile.nickname ?? '');
    setBio(profile.bio ?? '');
    setSpecialtyTags(profile.specialtyTags ?? []);
    setHomeSiteName(profile.homeSiteName ?? '');
    setDivingSinceYear(profile.divingSinceYear ? String(profile.divingSinceYear) : '');
    setStaticApnea(profile.freedivePersonalBests?.staticApneaSeconds != null ? String(profile.freedivePersonalBests.staticApneaSeconds) : '');
    setDynamicApnea(profile.freedivePersonalBests?.dynamicApneaMeters != null ? String(profile.freedivePersonalBests.dynamicApneaMeters) : '');
    setFreedivingDepth(profile.freedivePersonalBests?.depthMeters != null ? String(profile.freedivePersonalBests.depthMeters) : '');
    setPhotoPreview(profile.profilePhotoUrl ?? null);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  const coverInputRef = useRef<HTMLInputElement>(null);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);

  const handleCoverSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (coverInputRef.current) coverInputRef.current.value = '';
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      showError('Please choose a JPEG, PNG, or WEBP image');
      return;
    }
    setUploadingCover(true);
    setCoverPreview(URL.createObjectURL(file));
    try {
      // The server saves the cover key when it issues the upload URL, same as the avatar.
      await userProfileService.uploadPhoto(file, 'cover');
      showSuccess('Cover photo updated');
      onUpdated();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to upload cover photo');
      setCoverPreview(null);
    } finally {
      setUploadingCover(false);
    }
  };

  const handlePhotoSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      showError('Please choose a JPEG, PNG, or WEBP image');
      return;
    }

    setUploadingPhoto(true);
    setPhotoPreview(URL.createObjectURL(file));
    try {
      const photoKey = await userProfileService.uploadPhoto(file);
      const newPhotoUrl = profilePhotoUrl(photoKey);
      setPhotoPreview(newPhotoUrl);
      // The header renders its own avatar independent of this tab's profile prop,
      // so it needs its own signal to pick up the new photo without a full reload.
      window.dispatchEvent(new CustomEvent('profilePhotoUpdated', { detail: { photoUrl: newPhotoUrl } }));
      showSuccess('Photo uploaded');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to upload photo');
      setPhotoPreview(profile.profilePhotoUrl ?? null);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSave = async () => {
    setError(null);

    if (bio.length > 500) {
      setError('Bio must be 500 characters or fewer');
      return;
    }
    if (nickname.length > 40) {
      setError('Nickname must be 40 characters or fewer');
      return;
    }

    setSaving(true);
    try {
      const year = divingSinceYear.trim() ? parseInt(divingSinceYear, 10) : undefined;
      await userProfileService.updateOwnProfile({
        nickname: nickname.trim(),
        bio: bio.trim(),
        specialtyTags,
        homeSiteName: homeSiteName.trim() || undefined,
        homeSiteId: resolveHomeSiteId(homeSiteName, allSites),
        divingSinceYear: year,
        freedivePersonalBests: {
          staticApneaSeconds: staticApnea.trim() ? parseInt(staticApnea, 10) : undefined,
          dynamicApneaMeters: dynamicApnea.trim() ? parseFloat(dynamicApnea) : undefined,
          depthMeters: freedivingDepth.trim() ? parseFloat(freedivingDepth) : undefined,
        },
      });
      // Best-effort: the server cleared the old region when the home site changed; this tells it the new one.
      reportHomeRegion({ homeSiteId: resolveHomeSiteId(homeSiteName, allSites), homeSiteName: homeSiteName.trim() || undefined }, allSites).catch(() => {});
      showSuccess('Profile updated');
      onUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box display="flex" flexDirection="column" gap={2.5}>
      <Box>
        <Box
          sx={{
            height: 110,
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: 'action.hover',
            backgroundImage: (coverPreview ?? profile.coverPhotoUrl) ? `url(${coverPreview ?? profile.coverPhotoUrl})` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <Button
          size="small"
          sx={{ mt: 1 }}
          startIcon={uploadingCover ? <CircularProgress size={14} /> : <PhotoCamera />}
          disabled={uploadingCover}
          onClick={() => coverInputRef.current?.click()}
        >
          {uploadingCover ? 'Uploading…' : 'Change cover photo'}
        </Button>
        <input ref={coverInputRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={handleCoverSelected} />
      </Box>

      <Stack direction="row" spacing={2} alignItems="center">
        <Avatar src={photoPreview ?? undefined} sx={{ width: 64, height: 64, bgcolor: 'primary.main' }}>
          {!photoPreview && <Person />}
        </Avatar>
        <Button
          variant="outlined"
          startIcon={uploadingPhoto ? <CircularProgress size={16} /> : <PhotoCamera />}
          disabled={uploadingPhoto}
          onClick={() => fileInputRef.current?.click()}
        >
          {uploadingPhoto ? 'Uploading…' : 'Change Photo'}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          hidden
          onChange={handlePhotoSelected}
        />
      </Stack>

      <TextField
        label="Nickname"
        value={nickname}
        onChange={(e) => setNickname(e.target.value)}
        placeholder="What other divers see instead of your real name"
        helperText={`${nickname.length}/40 — leave blank to show your real name`}
      />

      <TextField
        label="Bio"
        value={bio}
        onChange={(e) => setBio(e.target.value)}
        placeholder="Tell other divers a bit about yourself"
        multiline
        minRows={3}
        fullWidth
        helperText={`${bio.length}/500`}
      />

      <Autocomplete
        multiple
        freeSolo
        options={SPECIALTY_SUGGESTIONS}
        value={specialtyTags}
        onChange={(_, value) => setSpecialtyTags(value.slice(0, 20))}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => <Chip label={option} {...getTagProps({ index })} key={option} />)
        }
        renderInput={(params) => (
          <TextField {...params} label="Specialties & Interests" placeholder="e.g. Wreck Diving" />
        )}
      />

      <Autocomplete
        freeSolo
        options={allSites.map(s => s.name)}
        value={homeSiteName}
        onInputChange={(_, value) => setHomeSiteName(value)}
        renderInput={(params) => (
          <TextField {...params} label="Home Dive Site" placeholder="Where do you usually dive?" />
        )}
      />

      <TextField
        label="Diving Since (year)"
        type="number"
        value={divingSinceYear}
        onChange={(e) => setDivingSinceYear(e.target.value)}
        inputProps={{ min: 1900, max: new Date().getFullYear() }}
      />

      <Typography variant="subtitle2" color="text.secondary">
        Freediving Personal Bests <Typography component="span" variant="caption">(optional)</Typography>
      </Typography>
      <Stack direction="row" spacing={2}>
        <TextField
          label="Static (sec)"
          type="number"
          value={staticApnea}
          onChange={(e) => setStaticApnea(e.target.value)}
          fullWidth
        />
        <TextField
          label="Dynamic (m)"
          type="number"
          value={dynamicApnea}
          onChange={(e) => setDynamicApnea(e.target.value)}
          fullWidth
        />
        <TextField
          label="Depth (m)"
          type="number"
          value={freedivingDepth}
          onChange={(e) => setFreedivingDepth(e.target.value)}
          fullWidth
        />
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      <Box>
        <Button variant="contained" onClick={handleSave} disabled={saving || uploadingPhoto}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </Box>
    </Box>
  );
};

export default ProfileEditTab;
